import { Redirect, Stack, router, useLocalSearchParams } from 'expo-router';
import { ArrowLeftIcon, ExternalLinkIcon } from 'lucide-react-native';
import * as Linking from 'expo-linking';
import * as React from 'react';

import { AppScreen, IconAction } from '@/components/ui/scene';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { useAuth } from '@/lib/auth/auth';
import { APP_CONFIG } from '@/lib/app/config';
import { getRouteEntityID } from '@/lib/navigation/routes';
import { buildBackstageURL } from '@/features/venues/venueBackstage';

export default function VenueClaimScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const venueID = getRouteEntityID(id);
  const session = useAuth((state) => state.session);
  const isHydrating = useAuth((state) => state.isHydrating);
  const claimURL = React.useMemo(
    () => buildBackstageURL(APP_CONFIG.backstageURL, 'claim', venueID),
    [venueID]
  );

  React.useEffect(() => {
    if (!session || !venueID) {
      return;
    }

    Linking.openURL(claimURL).catch(() => undefined);
  }, [claimURL, session, venueID]);

  const handleBack = React.useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    if (venueID) {
      router.replace(`/venues/${venueID}`);
      return;
    }
    router.replace('/(tabs)/events');
  }, [venueID]);

  if (isHydrating) {
    return <AppScreen />;
  }

  if (!session) {
    return (
      <Redirect
        href={{
          pathname: '/sign-in',
          params: { next: venueID ? `/venues/${venueID}/claim` : '/(tabs)/events' },
        }}
      />
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <AppScreen>
        <IconAction icon={ArrowLeftIcon} onPress={handleBack} />
        <Text className="mt-16 text-center text-3xl font-semibold text-foreground">
          Continue in Backstage
        </Text>
        <Text className="mx-auto mt-4 max-w-sm text-center text-base leading-7 text-muted-foreground">
          Venue claims now happen in Backstage so your review, team setup, and business tools all stay in one place.
        </Text>
        <Button
          className="mx-auto mt-8 w-full max-w-sm"
          onPress={() => Linking.openURL(claimURL).catch(() => undefined)}
        >
          <Text className="font-semibold text-primary-foreground">Open Backstage</Text>
          <Icon as={ExternalLinkIcon} size={16} className="text-primary-foreground" />
        </Button>
        <Text className="mx-auto mt-4 max-w-sm text-center text-sm leading-6 text-muted-foreground">
          If nothing opened, tap the button again and we&apos;ll bring you to the venue claim flow.
        </Text>
      </AppScreen>
    </>
  );
}
